import { useHubble } from '@seek/hubble';
import { Section, sendHotjarEvent, useConfig } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  Card,
  Column,
  Columns,
  IconThumb,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { useState } from 'react';
import { useParams } from 'react-router';
import type { SalariesTabModel } from '../../../pages/salaries/salariesTabModel';
import { metaBrand } from '../../MetaTag/meta';
import translations from './.vocab/index';
import { SalaryRangeInfo } from './SalaryRangeInfo';
interface SalariesByRoleSectionProps {
  model: Pick<SalariesTabModel, 'companyName' | 'salaryRanges'>;
}

export const SalariesByRoleSection = ({
  model,
}: SalariesByRoleSectionProps) => {
  const { t } = useTranslations(translations);
  const { country, brand } = useConfig();
  const params = useParams();
  const hubble = useHubble();
  const roleId = params.roleId;

  const [isFeedbackSent, setIsFeedbackSent] = useState(false);

  const { companyName, salaryRanges } = model;

  if (!salaryRanges) {
    return null;
  }

  const { lastUpdated } = salaryRanges;
  const roleSalaryRange = salaryRanges.salaryRangeByRoles?.find(
    (range) => range.roleId === roleId,
  );

  if (!roleSalaryRange) {
    return null;
  }

  // TODO: Phase 1 only shows salary range by company, to be cleanup at phase 2
  const { roleTitle, rangeByCompany } = roleSalaryRange;

  if (!rangeByCompany) {
    return null;
  }

  const { jobsCount, label: salaryRangeLabel, salaryType } = rangeByCompany;

  let salaryPeriod = '';
  switch (salaryType) {
    case 'ANNUAL_PACKAGE':
      salaryPeriod = t('per year');
      break;
    case 'MONTHLY':
      salaryPeriod = t('per month');
      break;
  }

  const description = t('The average salary based on past job ads');
  const tooltipText = t('This estimate is based on...', {
    brand: metaBrand[brand],
    jobsCount,
  });

  return (
    <Section
      mode={'view'}
      heading={t('role salary at companyName', {
        companyName,
        role: roleTitle,
      })}
      padding={'none'}
    >
      <Stack space="small">
        <Box boxShadow="small" borderRadius="large">
          <Card tone="formAccent">
            <SalaryRangeInfo
              salaryRange={salaryRangeLabel}
              salaryPeriod={salaryPeriod}
              description={description}
              tooltipText={tooltipText}
            />
          </Card>
        </Box>
        <Columns space={'none'}>
          {!isFeedbackSent ? (
            <Column>
              <Inline space="xsmall" alignY="center">
                <Text tone="secondary" size="xsmall">
                  {t('Was this helpful?')}
                </Text>
                <ButtonIcon
                  variant="transparent"
                  size="small"
                  icon={<IconThumb />}
                  label={t('Yes')}
                  onClick={() => {
                    sendHotjarEvent(`feedback-visible-salary-yes-${country}`);
                    setIsFeedbackSent(true);
                    hubble.trackEvent('component_pressed', {
                      component: {
                        name: 'thumbs up',
                        text: 'Was this helpful?',
                        type: 'buttonIcon',
                      },
                      currentPageSection: 'salaries role',
                    });
                  }}
                />
                <ButtonIcon
                  variant="transparent"
                  size="small"
                  icon={<IconThumb direction="down" />}
                  label={t('No')}
                  onClick={() => {
                    sendHotjarEvent(`feedback-visible-salary-no-${country}`);
                    setIsFeedbackSent(true);
                    hubble.trackEvent('component_pressed', {
                      component: {
                        name: 'thumbs down',
                        text: 'Was this helpful?',
                        type: 'buttonIcon',
                      },
                      currentPageSection: 'salaries role',
                    });
                  }}
                />
              </Inline>
            </Column>
          ) : null}
          <Column>
            <Text size="xsmall" align="right" tone="secondary">
              {t('Refreshed date', { date: lastUpdated })}
            </Text>
          </Column>
        </Columns>
        {isFeedbackSent && <Box id="hj-survey-container" padding={'none'} />}
      </Stack>
    </Section>
  );
};
